import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import Component from '../Component';
import { createFocusTrap } from '../utils/focusTrap';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import LoadingIndicator from './LoadingIndicator';
/**
 * The `ModalManager` component manages one or more modal dialogs. Stacking modals
 * is supported. Multiple dialogs can be shown at once; loading a new component
 * into the ModalManager will overwrite the previous one.
 */
export default class ModalManager extends Component {
  constructor() {
    super(...arguments);
    // Current focus trap
    _defineProperty(this, "focusTrap", void 0);
    // Keep track of the last set focus trap
    _defineProperty(this, "lastSetFocusTrap", void 0);
    // Keep track if there's an modal closing
    _defineProperty(this, "modalClosing", false);
    _defineProperty(this, "keyUpListener", null);
  }
  view(vnode) {
    return m('[', null, this.attrs.state.modalList.map((modal, i) => {
      const Tag = modal?.componentClass;
      return m("div", {
        key: modal.key,
        className: "ModalManager modal",
        "data-modal-key": modal.key,
        "data-modal-number": i,
        role: "dialog",
        "aria-modal": "true",
        style: !window.testing && {
          '--modal-number': i
        },
        "aria-hidden": this.attrs.state.modal !== modal && 'true'
      }, !!Tag && [m(Tag, Object.assign({
        key: modal.key
      }, modal.attrs, {
        animateShow: this.animateShow.bind(this),
        animateHide: this.animateHide.bind(this),
        state: this.attrs.state
      })), /* This backdrop is invisible and used for outside clicks to close the modal. */
      m("div", {
        key: modal.key,
        className: "ModalManager-invisibleBackdrop",
        onclick: this.handlePossibleBackdropClick.bind(this)
      })]);
    }), (this.attrs.state.backdropShown || this.attrs.state.loadingModal) && m("div", {
      className: "Modal-backdrop backdrop",
      ontransitionend: this.onBackdropTransitionEnd.bind(this),
      "data-showing": !!this.attrs.state.modalList.length || this.attrs.state.loadingModal,
      style: !window.testing && {
        '--modal-count': this.attrs.state.modalList.length + Number(this.attrs.state.loadingModal)
      }
    }, this.attrs.state.loadingModal && m(LoadingIndicator, null)));
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    this.keyUpListener = this.handleEscPress.bind(this);
    document.body.addEventListener('keyup', this.keyUpListener);
  }
  onbeforeremove(vnode) {
    super.onbeforeremove(vnode);
    this.keyUpListener && document.body.removeEventListener('keyup', this.keyUpListener);
    this.keyUpListener = null;
  }
  onupdate(vnode) {
    super.onupdate(vnode);
    requestAnimationFrame(() => {
      try {
        // Main content should gain or lose `aria-hidden` when modals are shown/removed
        // See: http://web-accessibility.carnegiemuseums.org/code/dialogs/

        if (!this.attrs.state.isModalOpen()) {
          document.getElementById('app')?.removeAttribute('aria-hidden');
          this.focusTrap.deactivate?.();
          clearAllBodyScrollLocks();
          return;
        }
        document.getElementById('app')?.setAttribute('aria-hidden', 'true');

        // Get current dialog key
        const dialogKey = this.attrs.state.modal.key;

        // Deactivate focus trap if there's a new dialog/closed
        if (this.focusTrap && this.lastSetFocusTrap !== dialogKey) {
          this.focusTrap.deactivate?.();
          clearAllBodyScrollLocks();
        }

        // Activate focus trap if there's a new dialog which is not trapped yet
        if (this.activeDialogElement && this.lastSetFocusTrap !== dialogKey) {
          this.focusTrap = createFocusTrap(this.activeDialogElement, {
            allowOutsideClick: true
          });
          this.focusTrap.activate?.();
          if (this.activeDialogManagerElement) {
            disableBodyScroll(this.activeDialogManagerElement, {
              reserveScrollBarGap: true
            });
          }
        }

        // Update key of current opened modal
        this.lastSetFocusTrap = dialogKey;
      } catch {
        // We can expect errors to occur here due to the nature of mithril rendering
      }
    });
  }

  /**
   * Get current active dialog
   */
  get activeDialogElement() {
    return document.body.querySelector(`.ModalManager[data-modal-key="${this.attrs.state.modal?.key}"] .Modal`);
  }

  /**
   * Get current active dialog
   */
  get activeDialogManagerElement() {
    return document.body.querySelector(`.ModalManager[data-modal-key="${this.attrs.state.modal?.key}"]`);
  }
  animateShow(readyCallback) {
    if (readyCallback === void 0) {
      readyCallback = () => {};
    }
    if (!this.attrs.state.modal) return;
    this.activeDialogElement?.addEventListener('transitionend', () => {
      readyCallback();
    }, {
      once: true
    });
    requestAnimationFrame(() => {
      this.activeDialogElement?.classList.add('in');
    });
  }
  animateHide(closedCallback) {
    if (closedCallback === void 0) {
      closedCallback = () => {};
    }
    if (this.modalClosing) return;
    this.modalClosing = true;
    const afterModalClosedCallback = () => {
      this.modalClosing = false;

      // Close the dialog
      this.attrs.state.close();
      closedCallback();
    };
    this.activeDialogElement?.addEventListener('transitionend', afterModalClosedCallback, {
      once: true
    });
    this.activeDialogElement?.classList.remove('in');
    this.activeDialogElement?.classList.add('out');
  }
  handleEscPress(e) {
    if (!this.attrs.state.modal) return;
    const dismissibleState = this.attrs.state.modal.componentClass.dismissibleOptions;

    // Close the dialog if the escape key was pressed
    // Check if closing via escape key is enabled
    if (e.key === 'Escape' && dismissibleState.viaEscKey) {
      e.preventDefault();
      this.animateHide();
    }
  }
  handlePossibleBackdropClick(e) {
    if (!this.attrs.state.modal || !this.attrs.state.modal.componentClass.dismissibleOptions.viaBackdropClick) return;
    this.animateHide();
  }
  onBackdropTransitionEnd(e) {
    if (e.propertyName === 'opacity') {
      const backdrop = e.currentTarget;
      if (backdrop.getAttribute('data-showing') === null) {
        // Backdrop is fading out
        this.attrs.state.backdropShown = false;
        m.redraw();
      }
    }
  }
}
flarum.reg.add('core', 'common/components/ModalManager', ModalManager);